import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Container, Typography, Card, CardContent, Chip, Button, Grid, Table, TableHead, TableBody, TableRow, TableCell, Select, MenuItem, CircularProgress, Alert, Switch, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { CONFIG_DEFAULTS, bustConfigCache } from '../hooks/useConfig';

const ADMIN_CARDS = [
  { emoji: '💳', title: 'Stripe Dashboard',     desc: 'View subscriptions, payments, refunds, and churn.',               action: () => window.open('https://dashboard.stripe.com', '_blank') },
  { emoji: '🟩', title: 'Supabase Dashboard',   desc: 'Database, Auth users, RLS policies, SQL editor.',                  action: () => window.open('https://supabase.com/dashboard/project/kgwevykxdlysfqotcuhd', '_blank') },
  { emoji: '🚀', title: 'Vercel Deployments',   desc: 'Deployment history, preview URLs, build logs, and env vars.',      action: () => window.open('https://vercel.com/footballfornerds', '_blank') },
  { emoji: '📁', title: 'Data Files',            desc: 'JSON files in /public/data — re-run scripts each season.',         action: null },
];

const PLAN_OPTIONS = ['free', 'nerds+', 'admin'];
const PLAN_COLORS  = { free: '#2ea84b', 'nerds+': '#f5a623', admin: '#c084fc' };

// ── NR card configs (legacy fantasy scoring — kept for reference) ─────────────
// eslint-disable-next-line no-unused-vars
const NR_CARD_CONFIGS = [
  {
    pos: 'qb', label: 'Quarterback', emoji: '🏈',
    fields: [
      { key: 'passTd',    label: 'Pass TD (pts)' },
      { key: 'passYards', label: 'Pass Yards mult' },
      { key: 'int',       label: 'INT (pts)' },
      { key: 'rushYards', label: 'Rush Yards mult' },
      { key: 'rushTd',    label: 'Rush TD (pts)' },
    ],
    minField: { key: 'minAttempts', label: 'Min Pass Attempts' },
  },
  {
    pos: 'rb', label: 'Running Back', emoji: '🏃',
    fields: [
      { key: 'rushYards',  label: 'Rush Yards mult' },
      { key: 'rushTd',     label: 'Rush TD (pts)' },
      { key: 'receptions', label: 'Receptions (pts)' },
      { key: 'recYards',   label: 'Rec Yards mult' },
    ],
    minField: { key: 'minCarries', label: 'Min Rush Carries' },
  },
  {
    pos: 'wr', label: 'Wide Receiver', emoji: '📡',
    fields: [
      { key: 'receptions', label: 'Receptions (pts)' },
      { key: 'recYards',   label: 'Rec Yards mult' },
      { key: 'recTd',      label: 'Rec TD (pts)' },
    ],
    minField: { key: 'minTargets', label: 'Min Targets' },
  },
  {
    pos: 'te', label: 'Tight End', emoji: '🔧',
    fields: [
      { key: 'receptions', label: 'Receptions (pts)' },
      { key: 'recYards',   label: 'Rec Yards mult' },
      { key: 'recTd',      label: 'Rec TD (pts)' },
    ],
    minField: { key: 'minTargets', label: 'Min Targets' },
  },
];

// ── Offensive NR weight configs ───────────────────────────────────────────────
const OFF_NR_WEIGHT_CONFIGS = [
  {
    pos: 'qb', label: 'QB Weights', emoji: '🏈',
    fields: [
      { key: '=completionPct',    label: 'Comp% weight',       hint: 'season total' },
      { key: 'passingYards',      label: 'Pass Yds/G weight' },
      { key: 'passingTouchdowns', label: 'Pass TD/G weight' },
      { key: '=~interceptionPct', label: 'INT% weight ↓',      hint: 'penalty — higher = more punishing' },
      { key: '~sacks',            label: 'Sacks/G weight ↓',   hint: 'penalty' },
      { key: 'rushingYards',      label: 'Rush Yds/G weight' },
      { key: 'rushingTouchdowns', label: 'Rush TD/G weight' },
      { key: '=passing_epa',       label: 'Pass EPA/G weight',  hint: 'advanced: expected points added' },
      { key: '=cpoe',             label: 'CPOE weight',        hint: 'advanced: completion % above expectation' },
      { key: '=~pressure_pct',    label: 'Pressure% weight ↓', hint: 'advanced penalty: % snaps under pressure' },
    ],
  },
  {
    pos: 'rb', label: 'RB Weights', emoji: '🏃',
    fields: [
      { key: 'rushingYards',                       label: 'Rush Yds/G weight' },
      { key: '=yardsPerRushAttempt',               label: 'Yds/Carry weight',         hint: 'season avg' },
      { key: 'rushingTouchdowns',                  label: 'Rush TD/G weight' },
      { key: 'receivingYards',                     label: 'Rec Yds/G weight' },
      { key: '=yardsPerReception',                 label: 'Yds/Rec weight',            hint: 'season avg' },
      { key: 'receivingTouchdowns',                label: 'Rec TD/G weight' },
      { key: '~fumbles',                           label: 'Fumbles/G weight ↓',        hint: 'penalty' },
      { key: '=rushing_epa',                        label: 'Rush EPA/G weight',         hint: 'advanced: expected points added' },
      { key: '=ybc_att',                           label: 'Yds Before Contact/Att ↑',  hint: 'advanced: PFR yards before contact per carry' },
      { key: '=rush_yards_over_expected_per_att',  label: 'Rush YOE/Att weight',       hint: 'advanced: NGS yards over expected per carry' },
    ],
  },
  {
    pos: 'wr', label: 'WR Weights', emoji: '📡',
    fields: [
      { key: 'receptions',          label: 'Rec/G weight' },
      { key: 'receivingYards',      label: 'Rec Yds/G weight' },
      { key: 'receivingTouchdowns', label: 'Rec TD/G weight' },
      { key: 'receivingFirstDowns', label: 'Rec 1st Downs/G weight' },
      { key: 'rushingYards',        label: 'Rush Yds/G weight' },
      { key: 'rushingTouchdowns',   label: 'Rush TD/G weight' },
      { key: '~fumbles',            label: 'Fumbles/G weight ↓',   hint: 'penalty' },
      { key: '=receiving_epa',       label: 'Rec EPA/G weight',     hint: 'advanced: expected points added' },
      { key: '=avg_separation',     label: 'Avg Separation weight', hint: 'advanced: NGS yards of separation at catch' },
      { key: '=~drop_percent',      label: 'Drop% weight ↓',        hint: 'advanced penalty: PFR drop rate' },
    ],
  },
  {
    pos: 'te', label: 'TE Weights', emoji: '🔧',
    fields: [
      { key: 'receptions',          label: 'Rec/G weight' },
      { key: 'receivingYards',      label: 'Rec Yds/G weight' },
      { key: 'receivingTouchdowns', label: 'Rec TD/G weight' },
      { key: 'receivingFirstDowns', label: 'Rec 1st Downs/G weight' },
      { key: 'rushingYards',        label: 'Rush Yds/G weight' },
      { key: 'rushingTouchdowns',   label: 'Rush TD/G weight' },
      { key: '~fumbles',            label: 'Fumbles/G weight ↓',   hint: 'penalty' },
      { key: '=receiving_epa',       label: 'Rec EPA/G weight',     hint: 'advanced: expected points added' },
      { key: '=avg_separation',     label: 'Avg Separation weight', hint: 'advanced: NGS yards of separation at catch' },
      { key: '=~drop_percent',      label: 'Drop% weight ↓',        hint: 'advanced penalty: PFR drop rate' },
    ],
  },
];

// ── Defensive NR weight configs ───────────────────────────────────────────────
const DEF_NR_WEIGHT_CONFIGS = [
  {
    pos: 'de', label: 'DE/Edge Weights', emoji: '🏹',
    fields: [
      { key: 'sacks',          label: 'Sacks/G weight' },
      { key: 'QBHits',         label: 'QB Hits/G weight' },
      { key: 'tacklesForLoss', label: 'TFL/G weight' },
      { key: 'totalTackles',   label: 'Tackles/G weight' },
      { key: 'forcedFumbles',  label: 'FF/G weight' },
      { key: '=prss',           label: 'Pressures/G weight', hint: 'advanced: PFR total pressures' },
      { key: '=hrry',           label: 'Hurries/G weight',   hint: 'advanced: PFR hurries' },
    ],
  },
  {
    pos: 'dt', label: 'DT Weights', emoji: '🧱',
    fields: [
      { key: 'sacks',          label: 'Sacks/G weight' },
      { key: 'QBHits',         label: 'QB Hits/G weight' },
      { key: 'tacklesForLoss', label: 'TFL/G weight' },
      { key: 'totalTackles',   label: 'Tackles/G weight' },
      { key: 'forcedFumbles',  label: 'FF/G weight' },
      { key: '=prss',           label: 'Pressures/G weight', hint: 'advanced: PFR total pressures' },
      { key: '=hrry',           label: 'Hurries/G weight',   hint: 'advanced: PFR hurries' },
    ],
  },
  {
    pos: 'lb', label: 'LB Weights', emoji: '🦬',
    fields: [
      { key: 'totalTackles',    label: 'Tackles/G weight' },
      { key: 'sacks',           label: 'Sacks/G weight' },
      { key: 'tacklesForLoss',  label: 'TFL/G weight' },
      { key: 'passesDefended',  label: 'PD/G weight' },
      { key: 'forcedFumbles',   label: 'FF/G weight' },
      { key: '=prss',            label: 'Pressures/G weight',   hint: 'advanced: PFR total pressures' },
      { key: '=~m_tkl_percent', label: 'Miss Tkl% weight ↓',   hint: 'advanced penalty: lower miss-tackle rate = better' },
    ],
  },
  {
    pos: 'cb', label: 'CB Weights', emoji: '🛡️',
    fields: [
      { key: 'passesDefended',  label: 'PD/G weight' },
      { key: 'interceptions',   label: 'INT/G weight' },
      { key: 'totalTackles',    label: 'Tackles/G weight' },
      { key: '=~yds_tgt',       label: 'Yds/Tgt Allowed ↓',  hint: 'advanced penalty: PFR yards per target allowed' },
      { key: '=~m_tkl_percent', label: 'Miss Tkl% weight ↓',  hint: 'advanced penalty: lower miss-tackle rate = better' },
    ],
  },
  {
    pos: 's', label: 'S Weights', emoji: '🔭',
    fields: [
      { key: 'totalTackles',    label: 'Tackles/G weight' },
      { key: 'passesDefended',  label: 'PD/G weight' },
      { key: 'interceptions',   label: 'INT/G weight' },
      { key: '=~yds_tgt',       label: 'Yds/Tgt Allowed ↓',  hint: 'advanced penalty: PFR yards per target allowed' },
      { key: '=~m_tkl_percent', label: 'Miss Tkl% weight ↓',  hint: 'advanced penalty: lower miss-tackle rate = better' },
    ],
  },
];

// ── Generic NR weight card ────────────────────────────────────────────────────
function NrWeightCard({ configKey, label, emoji, fields, initialValues, updatedAt: initUpdatedAt }) {
  const fallback = CONFIG_DEFAULTS[configKey] || {};
  const [values,    setValues]    = useState(initialValues || fallback);
  const [saving,    setSaving]    = useState(false);
  const [savedOk,   setSavedOk]   = useState(false);
  const [err,       setErr]       = useState(null);
  const [updatedAt, setUpdatedAt] = useState(initUpdatedAt || null);

  useEffect(() => {
    if (initialValues) setValues(initialValues);
    if (initUpdatedAt) setUpdatedAt(initUpdatedAt);
  }, [initialValues, initUpdatedAt]); // eslint-disable-line

  const handleChange = (key, raw) => {
    const num = parseFloat(raw);
    setValues(v => ({ ...v, [key]: isNaN(num) ? raw : num }));
  };

  const handleSave = async () => {
    setSaving(true); setErr(null); setSavedOk(false);
    const now = new Date().toISOString();
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Save timed out — your auth session may be broken. Sign out, clear cookies, and sign back in with one browser.')), 8000)
      );
      const save = supabase.from('site_config').upsert({
        key: configKey,
        value: values,
        updated_at: now,
        updated_by: 'admin',
      });
      const { error } = await Promise.race([save, timeout]);
      if (error) throw new Error(error.message);
      bustConfigCache();
      setSavedOk(true); setUpdatedAt(now); setTimeout(() => setSavedOk(false), 2000);
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.9)', border: '1px solid rgba(46,168,75,0.2)', borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>{emoji} {label}</Typography>
          {updatedAt && (
            <Typography sx={{ fontSize: '0.62rem', color: '#4a6080' }}>
              saved {new Date(updatedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#4a6080', mb: 1.5, lineHeight: 1.5 }}>
          Weight multiplier per stat rank (1 = equal weight, 2 = double influence, 0.5 = half). ↓ penalty stats: higher weight = more punishing.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          {fields.map(f => (
            <TextField
              key={f.key}
              label={f.label}
              type="number"
              value={values[f.key] ?? ''}
              onChange={e => handleChange(f.key, e.target.value)}
              size="small"
              inputProps={{ step: f.step ?? 0.1, min: 0 }}
              helperText={f.hint}
              sx={{
                ...INPUT_SX,
                '& .MuiFormHelperText-root': { color: '#4a6080', fontSize: '0.62rem', mt: 0.25 },
              }}
            />
          ))}
        </Box>

        {err && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem', py: 0.5 }}>{err}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
              bgcolor: '#2ea84b', color: '#050c16',
              '&:hover': { bgcolor: '#27963f' },
              '&:disabled': { bgcolor: 'rgba(46,168,75,0.3)', color: '#9eb8d0' },
            }}
          >
            {saving ? <CircularProgress size={14} sx={{ color: '#050c16' }} /> : 'Save Weights'}
          </Button>
          {savedOk && <Typography sx={{ fontSize: '0.72rem', color: '#10b981' }}>✓ saved</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

const SITE_FEATURE_LABELS = {
  constructionBanner: 'Construction Banner',
  fantasyHub:         'Fantasy Hub',
  faExplorer:         'Free Agent Explorer',
  draftExplorer:      'Draft Explorer',
  capEfficiency:      'Cap Efficiency',
  waiverWire:         'Waiver Wire (Fantasy)',
};

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    color: '#e8f0f8', fontSize: '0.8rem',
    backgroundColor: '#0a1220',
    '& fieldset': { borderColor: 'rgba(245,166,35,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(245,166,35,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#f5a623' },
  },
  '& .MuiInputLabel-root': { color: '#6a8aa8', fontSize: '0.75rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#f5a623' },
};

// ── NR config card (legacy — kept for reference) ─────────────────────────────
// eslint-disable-next-line no-unused-vars
function NrConfigCard({ pos, label, emoji, fields, minField, initialValues, updatedAt: initUpdatedAt }) {
  const defaultVals = CONFIG_DEFAULTS[`nr_formula_${pos}`] || {};
  const [values,    setValues]    = useState(initialValues || defaultVals);
  const [saving,    setSaving]    = useState(false);
  const [savedOk,   setSavedOk]   = useState(false);
  const [err,       setErr]       = useState(null);
  const [updatedAt, setUpdatedAt] = useState(initUpdatedAt || null);

  // Sync when parent loads fresh data
  useEffect(() => {
    if (initialValues) setValues(initialValues);
    if (initUpdatedAt) setUpdatedAt(initUpdatedAt);
  }, [initialValues, initUpdatedAt]); // eslint-disable-line

  const handleChange = (key, raw) => {
    const num = parseFloat(raw);
    setValues(v => ({ ...v, [key]: isNaN(num) ? raw : num }));
  };

  const handleSave = async () => {
    setSaving(true); setErr(null); setSavedOk(false);
    const now = new Date().toISOString();
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Save timed out — auth session may be broken. Sign out, clear cookies, sign back in with one browser only.')), 8000)
      );
      const save = supabase.from('site_config').upsert({
        key: `nr_formula_${pos}`,
        value: values,
        updated_at: now,
        updated_by: 'admin',
      });
      const { error } = await Promise.race([save, timeout]);
      if (error) throw new Error(error.message);
      bustConfigCache();
      setSavedOk(true); setUpdatedAt(now); setTimeout(() => setSavedOk(false), 2000);
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.9)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>{emoji} {label}</Typography>
          {updatedAt && (
            <Typography sx={{ fontSize: '0.62rem', color: '#4a6080' }}>
              saved {new Date(updatedAt).toLocaleString()}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
          {fields.map(f => (
            <TextField
              key={f.key}
              label={f.label}
              type="number"
              value={values[f.key] ?? ''}
              onChange={e => handleChange(f.key, e.target.value)}
              size="small"
              inputProps={{ step: 0.01 }}
              sx={INPUT_SX}
            />
          ))}
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            label={minField.label}
            type="number"
            value={values[minField.key] ?? ''}
            onChange={e => handleChange(minField.key, e.target.value)}
            size="small"
            fullWidth
            inputProps={{ step: 1, min: 0 }}
            sx={INPUT_SX}
          />
        </Box>

        {err && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem', py: 0.5 }}>{err}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
              bgcolor: '#f5a623', color: '#050c16',
              '&:hover': { bgcolor: '#e09520' },
              '&:disabled': { bgcolor: 'rgba(245,166,35,0.3)', color: '#9eb8d0' },
            }}
          >
            {saving ? <CircularProgress size={14} sx={{ color: '#050c16' }} /> : 'Save'}
          </Button>
          {savedOk && <Typography sx={{ fontSize: '0.72rem', color: '#10b981' }}>✓ saved</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Site features card ────────────────────────────────────────────────────────
function SiteFeaturesCard({ initialValues }) {
  const defaults = CONFIG_DEFAULTS.site_features;
  const [values,    setValues]    = useState(initialValues || defaults);
  const [saving,    setSaving]    = useState(false);
  const [savedOk,   setSavedOk]   = useState(false);
  const [err,       setErr]       = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (initialValues) setValues(initialValues);
  }, [initialValues]); // eslint-disable-line

  const saveFeatures = useCallback(async (newValues) => {
    setSaving(true); setErr(null); setSavedOk(false);
    const now = new Date().toISOString();
    const { error } = await supabase.from('site_config').upsert({
      key: 'site_features',
      value: newValues,
      updated_at: now,
      updated_by: 'admin',
    });
    if (error) setErr(error.message);
    else { setSavedOk(true); setUpdatedAt(now); setTimeout(() => setSavedOk(false), 2000); }
    setSaving(false);
  }, []);

  const handleToggle = (featureKey) => {
    const newValues = { ...values, [featureKey]: !values[featureKey] };
    setValues(newValues);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveFeatures(newValues), 500);
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.9)', border: '1px solid rgba(156,132,212,0.25)', borderRadius: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Feature Toggles</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {saving  && <CircularProgress size={14} sx={{ color: '#c084fc' }} />}
            {savedOk && <Typography sx={{ fontSize: '0.72rem', color: '#10b981' }}>✓ saved</Typography>}
            {updatedAt && <Typography sx={{ fontSize: '0.62rem', color: '#4a6080' }}>saved {new Date(updatedAt).toLocaleString()}</Typography>}
          </Box>
        </Box>

        {err && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem', py: 0.5 }}>{err}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {Object.entries(SITE_FEATURE_LABELS).map(([key, label]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#c0d4e8' }}>{label}</Typography>
              <Switch
                checked={!!values[key]}
                onChange={() => handleToggle(key)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#2ea84b' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2ea84b' },
                  '& .MuiSwitch-track': { backgroundColor: '#2a4560' },
                }}
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Admin config loader (NR + features together) ──────────────────────────────
function AdminConfigSection() {
  const [configRows, setConfigRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // 8-second timeout — if Supabase hangs (e.g. broken auth lock), fall through to defaults
    const timer = setTimeout(() => {
      setLoading(false);
      setLoadError('Config load timed out — showing defaults. Save will still write to Supabase.');
    }, 8000);

    supabase
      .from('site_config')
      .select('key, value, updated_at')
      .then(({ data, error }) => {
        clearTimeout(timer);
        if (error) throw error;
        const map = {};
        for (const row of data || []) map[row.key] = row;
        setConfigRows(map);
        setLoading(false);
      })
      .catch(err => {
        clearTimeout(timer);
        setLoadError(`Config load failed: ${err?.message || 'unknown error'} — showing defaults.`);
        setLoading(false);
      });

    return () => clearTimeout(timer);
  }, []);

  if (loading) return (
    <Box sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <CircularProgress size={16} sx={{ color: '#f5a623' }} />
      <Typography sx={{ fontSize: '0.8rem', color: '#6a8aa8' }}>Loading config…</Typography>
    </Box>
  );

  return (
    <>
      {loadError && (
        <Alert severity="warning" sx={{ mb: 3, fontSize: '0.78rem' }}>{loadError}</Alert>
      )}



        {/* ── Offensive NR Weights ─────────────────────────── */}
        <Box sx={{ mt: 5, mb: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#2ea84b', letterSpacing: '0.05em' }}>
            ⚔️ OFFENSIVE NR — STAT WEIGHTS
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#4a6080', mb: 2.5 }}>
            Controls how much each stat influences the rank-sum. Changes take effect immediately on next page load.
          </Typography>
          <Grid container spacing={3}>
            {OFF_NR_WEIGHT_CONFIGS.map(cfg => (
              <Grid item xs={12} md={6} key={cfg.pos}>
                <NrWeightCard
                  configKey={`nr_weights_off_${cfg.pos}`}
                  label={cfg.label}
                  emoji={cfg.emoji}
                  fields={cfg.fields}
                  initialValues={configRows[`nr_weights_off_${cfg.pos}`]?.value}
                  updatedAt={configRows[`nr_weights_off_${cfg.pos}`]?.updated_at}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ── Defensive NR Weights ─────────────────────────── */}
        <Box sx={{ mt: 5, mb: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#2ea84b', letterSpacing: '0.05em' }}>
            🛡️ DEFENSIVE NR — STAT WEIGHTS
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#4a6080', mb: 2.5 }}>
            Controls how much each stat influences the rank-sum per defensive position.
          </Typography>
          <Grid container spacing={3}>
            {DEF_NR_WEIGHT_CONFIGS.map(cfg => (
              <Grid item xs={12} md={6} key={cfg.pos}>
                <NrWeightCard
                  configKey={`nr_weights_def_${cfg.pos}`}
                  label={cfg.label}
                  emoji={cfg.emoji}
                  fields={cfg.fields}
                  initialValues={configRows[`nr_weights_def_${cfg.pos}`]?.value}
                  updatedAt={configRows[`nr_weights_def_${cfg.pos}`]?.updated_at}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

      {/* ── Site Features ───────────────────────────────── */}
      <Box sx={{ mt: 5, mb: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5, color: '#c084fc', letterSpacing: '0.05em' }}>
          🎛️ SITE FEATURES
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <SiteFeaturesCard
              initialValues={configRows['site_features']?.value}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Projection Settings ──────────────────────────── */}
      <Box sx={{ mt: 5, mb: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#f5a623', letterSpacing: '0.05em' }}>
          🎯 PROJECTION SETTINGS
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#4a6080', mb: 2.5 }}>
          Controls the matchup weight used in the Fantasy Projections engine. Changes take effect immediately on next page load.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <NrWeightCard
              configKey="projection_settings"
              label="Projection Settings"
              emoji="🎯"
              fields={[
                { key: 'matchupWeight', label: 'Matchup Weight', hint: '0.01 = ±15% max swing. Lower = softer matchup impact.', step: 0.001 },
              ]}
              initialValues={configRows['projection_settings']?.value}
              updatedAt={configRows['projection_settings']?.updated_at}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Team NR Scoring Weights ───────────────────────── */}
      <Box sx={{ mt: 5, mb: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#f5a623', letterSpacing: '0.05em' }}>
          🏆 TEAM NR — SCORING WEIGHTS
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#4a6080', mb: 2.5 }}>
          Controls how much team-level scoring (points scored / points allowed) influences the Team Offense and Defense NR rankings.
          Higher weight = points have more impact vs. individual player grades. 0 = ignore points entirely.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <NrWeightCard
              configKey="nr_weights_team_off"
              label="Team Offense NR — Pts Scored Weight"
              emoji="⚔️"
              fields={[
                { key: 'ptsWeight', label: 'Points Scored Weight', hint: 'How much "points scored per game" rank influences Team Offense NR. Default: 1.5', step: 0.1 },
              ]}
              initialValues={configRows['nr_weights_team_off']?.value}
              updatedAt={configRows['nr_weights_team_off']?.updated_at}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NrWeightCard
              configKey="nr_weights_team_def"
              label="Team Defense NR — Pts Allowed Weight"
              emoji="🛡️"
              fields={[
                { key: 'ptsWeight', label: 'Points Allowed Weight', hint: 'How much "points allowed per game" rank influences Team Defense NR. Default: 1.5', step: 0.1 },
              ]}
              initialValues={configRows['nr_weights_team_def']?.value}
              updatedAt={configRows['nr_weights_team_def']?.updated_at}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

// ── User management ───────────────────────────────────────────────────────────
function UserManagementPanel() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState({});
  const [error, setError]     = useState(null);
  const [saved, setSaved]     = useState({});

  useEffect(() => {
    supabase.from('profiles').select('*').order('created_at', { ascending: false })
      .then(({ data, error: err }) => {
        if (err) setError(err.message);
        else setUsers(data || []);
        setLoading(false);
      });
  }, []);

  const updatePlan = async (userId, newPlan) => {
    setSaving(s => ({ ...s, [userId]: true }));
    const { error: err } = await supabase.from('profiles').update({ plan: newPlan }).eq('id', userId);
    if (err) setError(err.message);
    else {
      setUsers(u => u.map(p => p.id === userId ? { ...p, plan: newPlan } : p));
      setSaved(s => ({ ...s, [userId]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [userId]: false })), 1500);
    }
    setSaving(s => ({ ...s, [userId]: false }));
  };

  if (loading) return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} sx={{ color: '#f5a623' }} /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{error} — run migration 002_profiles.sql in Supabase dashboard to enable user management.</Alert>}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#0e0e12', color: '#6a8aa8', fontSize: '0.72rem', fontWeight: 700, borderBottom: '1px solid rgba(245,166,35,0.25)', py: 1 } }}>
              <TableCell>Email</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} sx={{ '& td': { borderBottom: '1px solid rgba(245,166,35,0.06)', py: 0.75 }, '&:hover td': { bgcolor: '#1c1c24' } }}>
                <TableCell sx={{ fontSize: '0.78rem', color: '#c0d4e8' }}>{u.email || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem', color: '#9eb8d0' }}>{u.display_name || '—'}</TableCell>
                <TableCell>
                  <Chip label={u.plan} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', height: 18, bgcolor: PLAN_COLORS[u.plan] + '22', color: PLAN_COLORS[u.plan] || '#9eb8d0', border: `1px solid ${PLAN_COLORS[u.plan] || '#9eb8d0'}44` }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: '#4a6080' }}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Select size="small" value={u.plan} onChange={e => updatePlan(u.id, e.target.value)} disabled={saving[u.id]}
                      sx={{ fontSize: '0.72rem', height: 26, minWidth: 90, color: '#e8f0f8', bgcolor: '#131318', '& fieldset': { borderColor: 'rgba(245,166,35,0.3)' } }}>
                      {PLAN_OPTIONS.map(p => <MenuItem key={p} value={p} sx={{ fontSize: '0.75rem' }}>{p}</MenuItem>)}
                    </Select>
                    {saving[u.id] && <CircularProgress size={14} sx={{ color: '#f5a623' }} />}
                    {saved[u.id]  && <Typography sx={{ fontSize: '0.7rem', color: '#10b981' }}>✓ saved</Typography>}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !error && (
              <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: '#4a6080', py: 3, fontSize: '0.8rem' }}>No users yet. Profiles appear here after signup.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default function AdminPage() {
  const navigate  = useNavigate();
  const { isAdmin, authLoading } = useAuth();

  if (authLoading) return null;

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Navbar />
        <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '3rem', mb: 2 }}>🚫</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Access Denied</Typography>
          <Typography sx={{ color: '#9eb8d0', mb: 3 }}>This page is restricted to admin accounts.</Typography>
          <Button variant="contained" color="primary" onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>Go Home</Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 4 }}>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>🔧 Admin Panel</Typography>
          <Chip label="Admin" size="small" sx={{ bgcolor: '#3a1a6e', color: '#c084fc', fontWeight: 800 }} />
        </Box>

        <Grid container spacing={3}>
          {ADMIN_CARDS.map(card => (
            <Grid item xs={12} sm={6} md={4} key={card.title}>
              <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #2a4560', borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ fontSize: '2rem', mb: 1 }}>{card.emoji}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>{card.title}</Typography>
                  <Typography variant="body2" sx={{ color: '#8aaac8', mb: 2, lineHeight: 1.6 }}>{card.desc}</Typography>
                  {card.action && (
                    <Button variant="outlined" color="primary" size="small" onClick={card.action}
                      sx={{ textTransform: 'none', fontWeight: 600 }}>
                      Open →
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── NR Config + Site Features ─────────────────────────────── */}
        <AdminConfigSection />

        {/* ── User Management ───────────────────────────────────────── */}
        <Box sx={{ mt: 5, mb: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5, color: '#10b981', letterSpacing: '0.05em' }}>
            👥 USER MANAGEMENT
          </Typography>
          <Box sx={{ bgcolor: '#131318', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 2, overflow: 'hidden' }}>
            <UserManagementPanel />
          </Box>
        </Box>

        {/* ── Roadmap ───────────────────────────────────────────────── */}
        <Box sx={{ mt: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5, color: '#f5a623', letterSpacing: '0.05em' }}>
            🗺️ ROADMAP
          </Typography>
          {[
            {
              phase: 'THIS WEEK',
              color: '#10b981',
              items: [
                { label: 'Unlock top 10 FA Consensus Board for free users (tease → sign-up)', done: false },
                { label: 'Make Draft Big Board preview free (top 15 free, full 66 Nerds+)', done: false },
                { label: 'Wire Supabase Auth — sign-up/sign-in modal creates a real user', done: false },
              ],
            },
            {
              phase: 'NEXT 2 WEEKS',
              color: '#f5a623',
              items: [
                { label: 'Build Red Zone Tendencies — all 32 teams, pass%/run%/EPA/TD rate table (highly shareable)', done: false },
                { label: 'Build Team Style Profiles — radar charts of offensive/defensive identity (screenshottable)', done: false },
                { label: 'Update Nerd Ratings to 2025 season data (credibility fix)', done: false },
                { label: 'Stripe integration — actual subscription billing for Nerds+', done: false },
              ],
            },
            {
              phase: 'MONTH 2',
              color: '#c084fc',
              items: [
                { label: 'Situational QB Splits — 15 splits: shotgun/under center, pressure, down, quarter, game script', done: false },
                { label: 'Mobile optimization pass — responsive tables, better touch targets', done: false },
                { label: 'Player card social share — one-click share Nerd Rating card image for Twitter/X', done: false },
                { label: 'Drive EPA Explorer — EPA/play by starting field position, all 32 teams', done: false },
                { label: 'Position Comparison — side-by-side player comparison across all advanced metrics', done: false },
              ],
            },
            {
              phase: 'LONGER TERM',
              color: '#64748b',
              items: [
                { label: 'Blog/articles section — written analysis for organic SEO traffic', done: false },
                { label: 'Fantasy Projections — EPA + snap share + target share model', done: false },
                { label: 'Route Tree Analysis — WR/TE route type EPA breakdown', done: false },
                { label: 'Win Probability curves — in-game WP for any matchup', done: false },
                { label: 'Theory Tester — build/test football hypotheses against play-by-play', done: false },
                { label: 'Individual player pages — SEO-optimized permalinks per player', done: false },
              ],
            },
          ].map(section => (
            <Box key={section.phase} sx={{ mb: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box sx={{ height: 2, width: 18, bgcolor: section.color, borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, letterSpacing: '0.12em', color: section.color }}>
                  {section.phase}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: `${section.color}22` }} />
              </Box>
              <Box sx={{ pl: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {section.items.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                      mt: '3px', width: 14, height: 14, borderRadius: '3px', flexShrink: 0,
                      border: `2px solid ${item.done ? section.color : section.color + '55'}`,
                      bgcolor: item.done ? section.color : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {item.done && <Box sx={{ width: 6, height: 6, bgcolor: '#000', borderRadius: '1px' }} />}
                    </Box>
                    <Typography sx={{
                      fontSize: '0.8rem', color: item.done ? '#4a6080' : '#c0d4e8',
                      textDecoration: item.done ? 'line-through' : 'none', lineHeight: 1.5,
                    }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── Season Update Checklist ───────────────────────────────── */}
        <Box sx={{ mt: 5, p: 3, borderRadius: 2, backgroundColor: 'rgba(10,20,34,0.6)', border: '1px solid #3a1a6e' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#c084fc' }}>📋 Season Update Checklist</Typography>
          {[
            'Re-run /tmp/process_pbp.py → copy output to public/data/team_advanced_YYYY.json',
            'Re-run /tmp/process_player_advanced.py → copy to public/data/player_advanced_YYYY.json',
            'Re-run /tmp/process_phase3.py → copy to public/data/drive_advanced, qb_situational, rz_tendencies',
            'Update SEASON constant in PlayerStatsTable.js and TeamPage.js',
            'Update file references in TeamAdvancedStats.js, QBSituationalSplits.js, etc.',
            'Deploy: npx vercel --prod --yes',
          ].map((step, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'flex-start' }}>
              <Chip label={i + 1} size="small" sx={{ bgcolor: '#3a1a6e', color: '#c084fc', fontWeight: 700, fontSize: '0.7rem', minWidth: 26, height: 22 }} />
              <Typography variant="body2" sx={{ color: '#9eb8d0', lineHeight: 1.6 }}>{step}</Typography>
            </Box>
          ))}
        </Box>

      </Container>
    </Box>
  );
}
